import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { getTextField } from '@lender/utils';
import { Box } from '@mui/material';
import { onAutocompleteChangeInForm } from '@sharedCR/common/form';
import { AutocompleteCDS, DataFieldCDS } from '@sharedCR/coreDesignSystem/form';
import {
  checkIsInvalidProgramStatus,
  handleLoanFeesError,
  handleNotAcceptableResponse,
} from 'app/pages/loanApplication/LoanApplication.utils';
import { FormNames } from 'app/services/autoLockForms/autoLockForms.constants';
import { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { object } from 'yup';

import { FieldInputType } from 'constants/fields';
import { monthsOptions } from 'constants/options';
import { PendingChangesActionType } from 'constants/pendingChanges';
import useForm from 'hooks/useForm';
import useIsMounted from 'hooks/useIsMounted';
import {
  checkIsImpoundCollected,
  checkIsInvalidImpounds,
  getCalculatedImpoundFields,
  getFormattedAggregateAdjustment,
  getSchedule,
} from 'utils/impounds';
import { getPendingChangesInfo } from 'utils/loanFees';

import { showMessageAction } from 'store/global/actions';
import {
  editLoanImpoundsAction,
  getLoanFeesSummary,
  getLoanFeesViewAction,
} from 'store/loanApplications/actions';
import {
  selectApplicationEscrowData,
  selectFormHiddenFieldsMap,
  selectFormLockedFieldsMap,
  selectFormLockedPage,
  selectImpoundsFeesView,
  selectIsChattelLoanPurposeFromApplication,
  selectIsEscrowApplicable,
  selectLoanFeesSummary,
  selectTaxesInsuranceEscrowedLoanFeesView,
} from 'store/loanApplications/selectors';
import { selectExistingImpoundTypesOptions, selectImpoundTypes } from 'store/system/selectors';

import { LoanFeesTestIds } from '../../../../../../LoanFees.testIds';
import PendingChangesNumber from '../../../Label/PendingChangesNumber';
import TotalAmount from '../../../Label/TotalAmount';
import Controls from '../../Controls';
import useTriggerAddIfRequested from '../../hooks/useTriggerAddIfRequested';

import ImpoundList from './ImpoundList';
import { headerContainerStyles } from './ImpoundSection.styles';
import {
  checkIsImpoundConversionLocked,
  getDueDateDuplicates,
  getInitialImpounds,
  getIsNotDeleted,
  getSchemaImpounds,
  getValuesToSend,
  isFieldValueChanged,
} from './ImpoundSection.utils';

const schema = object().shape({
  set_impounds: getSchemaImpounds(),
  monthsCushion: getTextField(),
});

const ImpoundSection = ({ cdSection, isLoading, setEditedSection, setIsLoading, parentRef }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const [{ pendingChangesNumber, isTotalAmountPending }, setPendingChangesInfo] = useState({});

  const isMounted = useIsMounted();

  const impoundsData = useSelector(selectImpoundsFeesView);
  const taxesInsuranceEscrowed = useSelector(selectTaxesInsuranceEscrowedLoanFeesView);
  const isEscrowApplicable = useSelector(selectIsEscrowApplicable);
  const isChattelLoan = useSelector(selectIsChattelLoanPurposeFromApplication);
  const impoundsTypes = useSelector(selectImpoundTypes);
  const lockedFieldsMap = useSelector(selectFormLockedFieldsMap(FormNames.FEES));
  const hiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(FormNames.FEES));
  const isPageLocked = useSelector(selectFormLockedPage(FormNames.FEES));
  const allImpoundTypesOptions = useSelector(selectExistingImpoundTypesOptions);
  const { homeownersInsuranceImpoundId } = useSelector(selectApplicationEscrowData);
  const { firstPaymentDate: prepaidInterestCreditFirstPaymentDate } =
    useSelector(selectLoanFeesSummary);

  const isDisabledByLock = isLoading || isPageLocked;

  const {
    form,
    setForm,
    handleChange,
    validationErrors = {},
    setValidationErrors,
    setSchema,
    setInForm,
    setMultipleInForm,
    validateField,
    isFormChanged,
    validateForm,
    setIsTouched,
  } = useForm({
    set_impounds: getInitialImpounds({
      impounds: impoundsData.impounds,
      impoundsTypes,
      impoundsTypesOptions: allImpoundTypesOptions,
      homeownersInsuranceImpoundId,
      isImpoundConversionLocked: checkIsImpoundConversionLocked({ isPageLocked, lockedFieldsMap }),
      taxesInsuranceEscrowed,
    }),
    monthsCushion: { name: String(impoundsData.monthsCushion), value: impoundsData.monthsCushion },
  });

  useEffect(() => {
    setSchema(schema);
  }, [setSchema]);

  useEffect(() => {
    const pendingChangesInfo = getPendingChangesInfo(parentRef, false);
    setPendingChangesInfo(pendingChangesInfo);
  }, [form, parentRef]);

  useEffect(() => {
    setEditedSection(prev => ({ ...prev, isFormChanged, ref: parentRef }));
  }, [setEditedSection, isFormChanged, parentRef]);

  const { aggregateAdjustment, formattedAggregateAdjustment, isInvalidImpoundExists } =
    useMemo(() => {
      const existingImpounds = form.set_impounds.filter(getIsNotDeleted);
      const collectedImpounds = existingImpounds.filter(imp =>
        checkIsImpoundCollected(imp, taxesInsuranceEscrowed),
      );

      const aggregateAdjustment = prepaidInterestCreditFirstPaymentDate
        ? getSchedule({
            impounds: collectedImpounds,
            monthsCushion: form.monthsCushion?.value,
            prepaidInterestCreditFirstPaymentDate,
          })?.aggregateAdjustment
        : 0;
      const isInvalidImpoundExists = checkIsInvalidImpounds(existingImpounds, {
        prepaidInterestCreditFirstPaymentDate,
        isEscrowApplicable,
      });

      return {
        aggregateAdjustment,
        formattedAggregateAdjustment: getFormattedAggregateAdjustment({
          prepaidInterestCreditFirstPaymentDate,
          aggregateAdjustment,
          isInvalidImpoundExists,
        }),
        isInvalidImpoundExists,
      };
    }, [
      form.set_impounds,
      form.monthsCushion,
      prepaidInterestCreditFirstPaymentDate,
      isEscrowApplicable,
      taxesInsuranceEscrowed,
    ]);

  const totalAmount = useMemo(() => {
    return (
      _.sumBy(form.set_impounds, imp => {
        const { collectedAmount } = getCalculatedImpoundFields({
          monthsCollected: imp.monthsCollected?.value || 0,
          annualAmount: imp.annualAmount || 0,
        });

        return imp.pendingChangesActionType !== PendingChangesActionType.DELETED &&
          checkIsImpoundCollected(imp, taxesInsuranceEscrowed)
          ? Number(collectedAmount || 0)
          : 0;
      }) + aggregateAdjustment
    );
  }, [form.set_impounds, aggregateAdjustment, taxesInsuranceEscrowed]);

  const onAddImpound = useCallback(() => {
    setIsTouched(true);

    setForm(prev => ({
      ...prev,
      set_impounds: [
        ...prev.set_impounds,
        {
          isLocallyAdded: true,
          applicationImpoundId: crypto.randomUUID(),
          pendingChangesActionType: PendingChangesActionType.ADDED,
          amountDueDates: [],
        },
      ],
    }));
  }, [setForm, setIsTouched]);

  useTriggerAddIfRequested(setEditedSection, onAddImpound);

  const onDeleteImpound = useCallback(
    e => {
      const { impoundId } = e.currentTarget.dataset;

      setIsTouched(true);

      setForm(prev => ({
        ...prev,
        set_impounds: prev.set_impounds.reduce((acc, imp) => {
          if (imp.applicationImpoundId === impoundId && imp.isLocallyAdded) {
            return acc;
          }

          acc.push(
            imp.applicationImpoundId === impoundId
              ? {
                  ...imp,
                  amountDueDates: [],
                  isDeleted: true,
                  pendingChangesActionType: PendingChangesActionType.DELETED,
                }
              : imp,
          );

          return acc;
        }, []),
      }));
    },
    [setForm, setIsTouched],
  );

  const onCancelForm = useCallback(() => {
    setEditedSection(null);
  }, [setEditedSection]);

  const onSubmit = useCallback(
    async e => {
      e?.preventDefault?.();

      if (!(await validateForm({ form }))) {
        return;
      }

      const dueDateDuplicates = getDueDateDuplicates({
        formImpounds: form.set_impounds.filter(getIsNotDeleted),
      });

      if (dueDateDuplicates.length) {
        dispatch(
          showMessageAction({
            message: `Due Dates should be unique, see ${dueDateDuplicates.join(', ')}`,
            variant: 'error',
          }),
        );

        return;
      }

      const actionData = getValuesToSend({
        applicationId,
        form,
        initialImpounds: impoundsData.impounds,
        impoundsTypes,
        impoundsTypesOptions: allImpoundTypesOptions,
        homeownersInsuranceImpoundId,
        isChattelLoan,
        isEscrowed: taxesInsuranceEscrowed,
        isEscrowApplicable,
      });

      const handleSaveSuccess = () => {
        dispatch(getLoanFeesViewAction({ applicationId }));
        dispatch(getLoanFeesSummary({ applicationId }));

        if (isMounted) {
          setIsTouched(false);
        }

        setEditedSection(null);
      };

      setIsLoading(true);

      return dispatch(editLoanImpoundsAction(actionData))
        .then(handleSaveSuccess)
        .catch(({ response }) => {
          const isInvalidProgram = checkIsInvalidProgramStatus({ response });

          if (isInvalidProgram) {
            return handleNotAcceptableResponse({
              response,
              actionData,
              dispatch,
              onAcceptAction:
                (...args) =>
                () =>
                  dispatch(editLoanImpoundsAction(...args)).then(handleSaveSuccess),
              isSuccessActionNeededAfterAccept: true,
              isSaveWithoutProgramNeededOnNoPrograms: true,
            });
          }

          return handleLoanFeesError({ response, dispatch });
        })
        .finally(() => setIsLoading(false));
    },
    [
      validateForm,
      form,
      applicationId,
      impoundsData.impounds,
      impoundsTypes,
      allImpoundTypesOptions,
      homeownersInsuranceImpoundId,
      isChattelLoan,
      taxesInsuranceEscrowed,
      isEscrowApplicable,
      setIsLoading,
      dispatch,
      isMounted,
      setEditedSection,
      setIsTouched,
    ],
  );

  useEffect(() => {
    parentRef.current.onCancelForm = onCancelForm;
    parentRef.current.onSubmit = onSubmit;
  }, [onCancelForm, onSubmit, parentRef]);

  const meta = useMemo(
    () => ({
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setMultipleInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setValidationErrors,
      setEditedSection,
    }),
    [
      handleChange,
      isFormChanged,
      validateField,
      validateForm,
      setInForm,
      setMultipleInForm,
      setForm,
      setIsTouched,
      validationErrors,
      setValidationErrors,
      setEditedSection,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  return (
    <>
      <Box sx={headerContainerStyles}>
        <DataFieldCDS
          disabled
          fullWidth
          id="totalImpounds"
          label="Total Impounds"
          name="totalImpounds"
          placeholder="TBD"
          type={FieldInputType.CURRENCY}
          value={!isInvalidImpoundExists ? totalAmount : null}
        />

        <AutocompleteCDS
          disableClearable
          fullWidth
          required
          dataTestId={LoanFeesTestIds.IMPOUND_MONTHS_CUSHION_SELECT}
          disabled={isDisabledByLock || lockedFieldsMap.monthsCushion}
          error={!!validationErrors.monthsCushion}
          helperText={validationErrors.monthsCushion}
          id="monthsCushion"
          isFieldChanged={isFieldValueChanged({
            value: form.monthsCushion?.value,
            initialValue: impoundsData.monthsCushion,
            modifiedFields: impoundsData.modifiedFields,
            prop: 'monthsCushion',
          })}
          isHidden={hiddenFieldsMap.monthsCushion}
          label="Months Cushion"
          name="monthsCushion"
          options={monthsOptions}
          value={form.monthsCushion}
          onChange={onAutocompleteChange}
        />

        <DataFieldCDS
          allowNegative
          disabled
          fullWidth
          id="aggregateAdjustment"
          label="Aggregate Adjustments"
          name="aggregateAdjustment"
          placeholder="TBD"
          type={FieldInputType.CURRENCY}
          value={formattedAggregateAdjustment !== 'TBD' ? formattedAggregateAdjustment : null}
        />
      </Box>

      <ImpoundList
        formData={form.set_impounds}
        isLoading={isLoading}
        meta={meta}
        monthsCushion={form.monthsCushion?.value}
        setIsLoading={setIsLoading}
        onAddImpound={onAddImpound}
        onDeleteImpound={onDeleteImpound}
      />

      {createPortal(
        <TotalAmount
          isPending={isTotalAmountPending}
          value={!isInvalidImpoundExists ? totalAmount : 0}
        />,
        document.getElementById(`total-loan-fee-section-${cdSection}`),
      )}

      {createPortal(
        <PendingChangesNumber value={pendingChangesNumber} />,
        document.getElementById(`pending-changes-number-${cdSection}`),
      )}

      <Controls
        isFormChanged={isFormChanged}
        isLoading={isLoading}
        onCancelForm={onCancelForm}
        onSubmit={onSubmit}
      />
    </>
  );
};

ImpoundSection.propTypes = {
  ...WithLoadingPropTypes,
  cdSection: PropTypes.string.isRequired,
  setEditedSection: PropTypes.func.isRequired,
  parentRef: PropTypes.shape({ current: PropTypes.object }).isRequired,
};

export default ImpoundSection;
